import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../helpers/apiHelper';
import { RiShieldCheckLine, RiShieldLine, RiFileCopyLine, RiQrCodeLine } from 'react-icons/ri';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { useOrganization } from '../../../helpers/api/organization/organization';

const backendUrl =
  (window as any).RUNTIME_ENV?.BACKEND_URL ??
  process.env.BACKEND_URL ??
  'http://localhost:8000/api/v1';

interface ApiResponse<T = any> {
  status: string;
  message: string;
  data: T;
  datetime: string;
  status_code: number;
}

const TwoFactorAuthSettings = ({ user }) => {
  const { t } = useTranslation('AccountPage');
  const { data: organizationData, isLoading: isOrgLoading } = useOrganization();
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [is2faEnabled, setIs2faEnabled] = useState(user.is_2fa_enabled);
  const [showConfirm, setShowConfirm] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [disableError, setDisableError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);

  const isForce2FAEnabled = organizationData?.force_2fa_enabled || false;

  // Start 2FA setup and get the secret
  const startSetup = async () => {
    setError('');
    setMessage('');
    try {
      const res = await apiClient.post('/2fa/setup', {}) as unknown as ApiResponse<{secret: string, uri: string}>;
      setSetupData(res.data);
    } catch (e: any) {
      setError(e?.message || t('twoFactor.setupFailed'));
    }
  };

  // Verify the code entered by the user
  const verifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      const res = await apiClient.post('/2fa/verify-setup', { code }) as unknown as ApiResponse<{success: boolean}>;
      setMessage(res.message || t('twoFactor.enabledSuccess'));
      setSetupData(null);
      setCode('');
      setIs2faEnabled(true);
      // Optionally, trigger a user data refresh here
    } catch (e: any) {
      setError(e?.message || t('twoFactor.verifyFailed'));
    }
  };

  // Disable 2FA with TOTP code
  const disable2fa = async () => {
    setDisableError('');
    setDisableLoading(true);
    try {
      const res = await apiClient.post('/2fa/disable', { code: disableCode }) as unknown as ApiResponse<{success: boolean}>;
      setMessage(res.message || t('twoFactor.disabledSuccess'));
      setIs2faEnabled(false);
      setSetupData(null);
      setCode('');
      setShowConfirm(false);
      setDisableCode('');
    } catch (e: any) {
      setDisableError(e?.message || t('twoFactor.disableFailed'));
    }
    setDisableLoading(false);
  };

  // Confirmation dialog for disabling 2FA
  if (showConfirm) {
    return (
      <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-md">
        <div className="text-center mb-6">
          <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
            <RiShieldLine className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
            {t('twoFactor.disableTitle')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('twoFactor.disableDescription')}
          </p>
        </div>

        <div className="space-y-4">
          <FormField label={t('twoFactor.authCode')} htmlFor="disable-code">
            <Input
              id="disable-code"
              type="text"
              value={disableCode}
              onChange={e => setDisableCode(e.target.value)}
              maxLength={6}
              minLength={6}
              placeholder="123456"
              className="text-center text-lg tracking-wider"
            />
          </FormField>

          {disableError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{disableError}</p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={disable2fa}
              variant="destructive"
              size="md"
              className="flex-1"
              disabled={disableLoading || disableCode.length !== 6}
            >
              <RiShieldLine className="w-4 h-4 mr-2" />
              {disableLoading ? t('twoFactor.disabling') : t('twoFactor.disable')}
            </Button>
            <Button
              onClick={() => { setShowConfirm(false); setDisableCode(''); setDisableError(''); }}
              variant="secondary"
              size="md"
              className="flex-1"
              disabled={disableLoading}
            >
              {t('twoFactor.cancel')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // UI for when 2FA is enabled
  if (is2faEnabled) {
    return (
      <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-md">
        <div className="text-center mb-6">
          <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
            <RiShieldCheckLine className="w-6 h-6 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
            {t('twoFactor.title')}
          </h3>
          <div className="flex items-center justify-center gap-2 mb-4">
            <span className="text-sm text-gray-500 dark:text-gray-400">{t('twoFactor.status')}</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1"></div>
              {t('twoFactor.enabled')}
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('twoFactor.protectedMessage')}
          </p>
        </div>

        <div className="space-y-4">
          {message && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-600 dark:text-green-400">{message}</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isOrgLoading ? (
            <Button
              variant="destructive"
              size="md"
              className="w-full"
              disabled={true}
            >
              <RiShieldLine className="w-4 h-4 mr-2" />
              {t('twoFactor.loading')}
            </Button>
          ) : !isForce2FAEnabled ? (
            <Button
              onClick={() => setShowConfirm(true)}
              variant="destructive"
              size="md"
              className="w-full"
            >
              <RiShieldLine className="w-4 h-4 mr-2" />
              {t('twoFactor.disable')}
            </Button>
          ) : (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                <RiShieldCheckLine className="w-4 h-4 inline mr-2" />
                {t('twoFactor.orgRequiredMessage')}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // UI for manual setup
  if (setupData) {
    const handleCopySecret = () => {
      navigator.clipboard.writeText(setupData.secret);
      // You could add a toast notification here
    };

    return (
      <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-lg">
        <div className="text-center mb-6">
          <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
            <RiShieldCheckLine className="w-6 h-6 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
            {t('twoFactor.setupTitle')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('twoFactor.setupSubtitle')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Secret Key */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-title-page-color-100 dark:text-white flex items-center">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold mr-2">1</span>
              {t('twoFactor.step1')}
            </h4>
            <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 flex items-center justify-between">
              <code className="text-sm font-mono text-title-page-color-100 dark:text-white tracking-wider break-all">
                {setupData.secret}
              </code>
              <Button
                onClick={handleCopySecret}
                variant="ghost"
                size="sm"
                className="ml-2 flex-shrink-0"
              >
                <RiFileCopyLine className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Step 2: QR Code */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-title-page-color-100 dark:text-white flex items-center">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold mr-2">2</span>
              {t('twoFactor.step2')}
            </h4>
            <div className="flex justify-center">
              <div className="bg-white p-4 rounded-lg border border-gray-200 dark:border-gray-600">
                <img
                  src={`${backendUrl}/2fa/qr`}
                  alt={t('twoFactor.qrAlt')}
                  className="w-44 h-44"
                />
              </div>
            </div>
          </div>

          {/* Step 3: Verify */}
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-title-page-color-100 dark:text-white flex items-center">
              <span className="inline-flex items-center justify-center w-5 h-5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-xs font-bold mr-2">3</span>
              {t('twoFactor.step3')}
            </h4>
            <form onSubmit={verifyCode} className="space-y-4">
              <FormField label={t('twoFactor.authCode')} htmlFor="setup-code">
                <Input
                  id="setup-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  maxLength={6}
                  minLength={6}
                  placeholder="123456"
                  className="text-center text-lg tracking-wider"
                />
              </FormField>

              <Button
                type="submit"
                variant="primary"
                size="md"
                className="w-full"
                disabled={code.length !== 6}
              >
                <RiShieldCheckLine className="w-4 h-4 mr-2" />
                {t('twoFactor.verifyEnable')}
              </Button>
            </form>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {message && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
              <p className="text-sm text-green-600 dark:text-green-400">{message}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // UI for when 2FA is disabled
  return (
    <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 max-w-md">
      <div className="text-center mb-6">
        <div className="mx-auto w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center mb-4">
          <RiShieldLine className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
        </div>
        <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
          {t('twoFactor.title')}
        </h3>
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">{t('twoFactor.status')}</span>
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400">
            <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-1"></div>
            {t('twoFactor.disabled')}
          </span>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('twoFactor.disabledDescription')}
        </p>
      </div>

      <div className="space-y-4">
        {message && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3">
            <p className="text-sm text-green-600 dark:text-green-400">{message}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <Button
          onClick={startSetup}
          variant="primary"
          size="md"
          className="w-full"
        >
          <RiShieldCheckLine className="w-4 h-4 mr-2" />
          {t('twoFactor.enable')}
        </Button>
      </div>
    </div>
  );
};

export default TwoFactorAuthSettings;